/**
 * 系统通知 extraData 解析与工作流通知跳转口径（HeaderBar 面板 / 通知中心页共用）
 *
 * 数据口径（见 api-code/docs/updates/wf-engine-notify-design.md）：
 * - 系统通知（会话 sys_wf_<userId>）的 WS 投递帧携带 extraData（对象或 JSON 字符串），
 *   内含 notifyType / bizType / bizId / event / link / bizMessageId 等结构化字段；
 * - **落库的 chs_message 不存 extraData**，故历史消息无结构化字段，跳转仅对「本次会话内
 *   实时收到」的通知生效（stores/message.ts 把帧里的 extraData 按会话 / 消息 id 缓存）；
 * - **2026-10-02 起 chs_message 新增持久化 link 列**（alter_chs_message_add_link.sql），
 *   历史通知读取后直接携带 link，跳转解析链最前面优先使用（见 pushByNotifyLink）。
 *
 * 跳转规则：
 * - notifyType 以 `WF_` 开头（WF_TODO / WF_RESULT / WF_TIMEOUT / WF_CC）视为审批通知；
 * - 实例 id 解析优先级：extraData.instanceId → link 中的 instanceId=数字 / instance/数字 →
 *   无则携带 bizType + bizId 交给审批中心页反查（同一 bizType + bizId 唯一实例）。
 */

import router from '@/router';

/** extraData 统一为对象（解析失败返回 null） */
export function parseNotifyExtra(extra: unknown): Record<string, any> | null {
    if (!extra) return null;
    if (typeof extra === 'object') return extra as Record<string, any>;
    if (typeof extra === 'string') {
        try {
            const parsed = JSON.parse(extra);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
            return null;
        }
    }
    return null;
}

/** 是否为审批（工作流）类系统通知 */
export function isWfNotify(extra: Record<string, any> | null): boolean {
    if (!extra) return false;
    const notifyType = String(extra.notifyType || '');
    if (notifyType.startsWith('WF_')) return true;
    const link = String(extra.link || '');
    return link.includes('approval') || link.includes('wf/instance');
}

/**
 * 从 extraData 直接解析实例 id（解析不到返回 NaN，调用方应走 bizType + bizId 反查兜底）
 */
export function resolveWfInstanceId(extra: Record<string, any> | null): number {
    if (!extra) return NaN;
    if (extra.instanceId !== undefined && extra.instanceId !== null && extra.instanceId !== '') {
        const id = Number(extra.instanceId);
        if (Number.isFinite(id) && id > 0) return id;
    }
    const link = String(extra.link || '');
    const matched = link.match(/instanceId=(\d+)/) || link.match(/instance\/(\d+)/);
    if (matched) return Number(matched[1]);
    return NaN;
}

/**
 * 持久化/帧内 link 直达：link 为站内路由（如 /admin/approval/index?instanceId=5）时
 * 直接 router.push 并返回 true；空串或非站内路由返回 false（调用方继续走
 * extraData 缓存 / bizId 反查兜底）。
 *
 * 审批中心路由统一补 autoOpen=1，与既有直达口径一致（HeaderBar#goApprovalDetail），
 * 后端生成的 WF 通知 link 即该格式（见 api-code WfNotifySupport#resolveLink）。
 */
export function pushByNotifyLink(link?: unknown): boolean {
    const value = String(link || '').trim();
    if (!value.startsWith('/')) return false;
    if (value.startsWith('/admin/approval/index')) {
        const [path, queryString] = value.split('?');
        const query: Record<string, string> = {};
        new URLSearchParams(queryString || '').forEach((v, k) => {
            query[k] = v;
        });
        query.autoOpen = '1';
        router.push({ path, query });
        return true;
    }
    router.push(value);
    return true;
}
